import { Injectable } from '@angular/core';

export type SpellCheckLanguage = 'MK' | 'EN' | 'AL';

@Injectable({
  providedIn: 'root'
})
export class SpellcheckService {
  private spellCheckers: Map<SpellCheckLanguage, any> = new Map();
  private initialized: Map<SpellCheckLanguage, boolean> = new Map();

  constructor() {}

  async initializeLanguage(lang: SpellCheckLanguage): Promise<void> {
    if (this.initialized.get(lang)) {
      return;
    }

    try {
      const langCode = lang.toLowerCase();

      const [affResponse, dicResponse] = await Promise.all([
        fetch(`/assets/dictionaries/${langCode}.aff`),
        fetch(`/assets/dictionaries/${langCode}.dic`)
      ]);

      if (!affResponse.ok || !dicResponse.ok) {
        throw new Error(`Failed to load dictionary for ${lang}`);
      }

      const [affText, dicText] = await Promise.all([
        affResponse.text(),
        dicResponse.text()
      ]);

      const nspell = (await import('nspell')).default;

      const checker = nspell(affText, dicText);
      this.spellCheckers.set(lang, checker);
      this.initialized.set(lang, true);
    } catch (error) {
      console.error(`Failed to initialize spellcheck for ${lang}:`, error);
    }
  }

  isCorrect(word: string, lang: SpellCheckLanguage): boolean {
    const checker = this.spellCheckers.get(lang);
    if (!checker) {
      return true;
    }
    return checker.correct(word);
  }

  getSuggestions(word: string, lang: SpellCheckLanguage): string[] {
    const checker = this.spellCheckers.get(lang);
    if (!checker) {
      return [];
    }
    return checker.suggest(word);
  }

  checkText(text: string, lang: SpellCheckLanguage): Array<{word: string, index: number, suggestions: string[]}> {
    const checker = this.spellCheckers.get(lang);
    if (!checker) {
      return [];
    }

    const words = this.tokenize(text);
    const misspelled: Array<{word: string, index: number, suggestions: string[]}> = [];

    words.forEach(({word, index}) => {
      if (!checker.correct(word)) {
        misspelled.push({
          word,
          index,
          suggestions: checker.suggest(word)
        });
      }
    });

    return misspelled;
  }

  private tokenize(text: string): Array<{word: string, index: number}> {
    const regex = /\p{L}+/gu;
    const tokens: Array<{word: string, index: number}> = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      tokens.push({
        word: match[0],
        index: match.index
      });
    }

    return tokens;
  }
}
